import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { endOfDay, isAfter, subDays, subYears } from 'date-fns';
import { get as _get } from 'lodash';
import { ClaimState } from '../../../types/type';
import { isAutoClaim } from '../../../utils/Policy-Util';
import { parseAndFormatDate } from '../../../utils/Utilities';

const initialState: ClaimState = {
  openClaims: [],
  closedClaims: [],
  draftClaims: [],
  hasHiddenClaims: false,
  hasClaimsRetrievalIssue: false,
};

const claimsSlice = createSlice({
  name: 'claims',
  initialState,
  reducers: {
    updateClaimsState(state, action: PayloadAction<{ response: any }>) {
      // Handle both { response: [...] } and direct array formats
      const claimsData = action.payload.response || action.payload;

      /*
      A degraded call can resolve with no data at all, and sortClaims filters the payload
      directly. Bail out rather than throwing on a non-array.
      */
      if (!Array.isArray(claimsData)) {
        return;
      }

      const sorted = sortClaims(claimsData);
      state.openClaims = sorted.openClaims;
      state.closedClaims = sorted.closedClaims;
      state.draftClaims = sorted.draftClaims;
      state.hasHiddenClaims = sorted.hasHiddenClaims;
    },
    /*
    Set on every completed claims-by-policy call, including clean successes, so a healthy
    refetch clears a banner left over from an earlier degraded call.
    */
    setClaimsRetrievalIssue(state, action: PayloadAction<boolean>) {
      state.hasClaimsRetrievalIssue = action.payload;
    },
    addDraftClaim(state, action: PayloadAction<{ draft: any }>) {
      state.draftClaims?.push(action.payload.draft);
    },
    deleteDraftClaim(state, action: PayloadAction<{ claimNumber: string }>) {
      const indexToRemove = _get(state, 'draftClaims', []).findIndex(
        (fnol: any) => fnol.claimNumber === action.payload.claimNumber
      );
      state.draftClaims?.splice(indexToRemove, 1);
    },
    addSubmittedClaim(state, action: PayloadAction<{ claims: any }>) {
      // TODO: fill out claims
    },
  },
});

function sortClaims(getClaimsV3Reponse: any) {
  const thirtyDaysAgo = endOfDay(subDays(new Date(), 31));
  const minDate = subYears(new Date(), 10);

  let hasHiddenClaims = false;

  const draftClaims = getClaimsV3Reponse.filter((fnol: any) => {
    const { date: openDate, formatted: openDateFormatted } = parseAndFormatDate(_get(fnol, 'openDate', ''));
    const { date: lossDate, formatted: lossDateFormatted } = parseAndFormatDate(_get(fnol, 'lossDate', ''));
    fnol.openDate = openDateFormatted;
    fnol.lossDate = lossDateFormatted;

    const firstPartyClaim = !isAutoClaim(_get(fnol, 'policyType', '')) || isAutoClaim(_get(fnol, 'policyType', ''));
    return (
      firstPartyClaim &&
      isAfter(openDate, thirtyDaysAgo) &&
      _get(fnol, 'claimNumber', '').substring(0, 2) === 'TC' &&
      _get(fnol, 'newDigitalClaim', false)
    );
  });

  const claims = getClaimsV3Reponse.filter((claim: any) => {
    const { date: openDate, formatted: openDateFormatted } = parseAndFormatDate(_get(claim, 'openDate', ''));
    const { date: lossDate, formatted: lossDateFormatted } = parseAndFormatDate(_get(claim, 'lossDate', ''));
    claim.openDate = openDateFormatted;
    claim.lossDate = lossDateFormatted;

    // Filter out ICS, void, TC, and claims older than 10 years
    if (
      _get(claim, 'claimSystem', '').toLowerCase() !== 'ics' &&
      isAfter(openDate, minDate) &&
      _get(claim, 'closedReasonTypeCode', '').toLowerCase() !== 'void' &&
      _get(claim, 'claimNumber', '').substring(0, 2) !== 'TC'
    ) {
      return true;
    } else {
      hasHiddenClaims =
        hasHiddenClaims ||
        (_get(claim, 'closedReasonTypeCode', '').toLowerCase() !== 'void' &&
          _get(claim, 'claimNumber', '').substring(0, 2) !== 'TC');
      return false;
    }
  });

  const closedClaims = claims.filter((claim: any) => {
    return (
      _get(claim, 'claimStatusCode', '').toUpperCase() === 'ARCHIVED' ||
      _get(claim, 'claimStatusCode', '').toUpperCase() === 'CLOSED' ||
      _get(claim, 'claimStatusCode', '').toUpperCase() === 'VOID' ||
      _get(claim, 'claimStatusCode', '').toUpperCase() === 'CLOSED_AUTO_PAY' ||
      _get(claim, 'claimStatusCode', '').toUpperCase() === 'CLOSED_ERROR' ||
      _get(claim, 'claimStatusCode', '').toUpperCase() === 'REOPEN'
    );
  });

  const openClaims = claims.filter((claim: any) => {
    return (
      _get(claim, 'claimStatusCode', '').toUpperCase() === 'OPEN' ||
      _get(claim, 'claimStatusCode', '').toUpperCase() === 'IN_PROGRESS' ||
      _get(claim, 'claimStatusCode', '').toUpperCase() === 'OPEN_REPORTED' ||
      _get(claim, 'claimStatusCode', '').toUpperCase() === 'AUTOPAY_PENDING' ||
      _get(claim, 'claimStatusCode', '').toUpperCase() === 'AUTOPAY_REVIEW' ||
      _get(claim, 'claimStatusCode', '').toUpperCase() === 'AWAITING_SETUP'
    );
  });

  return { draftClaims, openClaims, closedClaims, hasHiddenClaims };
}

export const { updateClaimsState, deleteDraftClaim, addDraftClaim, setClaimsRetrievalIssue } = claimsSlice.actions;
export default claimsSlice.reducer;
